import React, { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Check, Loader2, RefreshCw, XCircle } from "lucide-react";

/**
 * BookingSuccess — landed here after Stripe Checkout redirects back
 * with `session_id` and `booking_id` in the query string. Polls
 * `GET /api/bookings/{id}/payment-status` every 2s for up to 30s
 * until Stripe reports `payment_status="paid"`.
 */
export default function BookingSuccess() {
  const [params] = useSearchParams();
  const bookingId = params.get("booking_id");
  const sessionId = params.get("session_id");
  const navigate = useNavigate();

  const [phase, setPhase] = useState("polling"); // polling | paid | timeout | error
  const [errorMsg, setErrorMsg] = useState("");
  const [booking, setBooking] = useState(null);
  const cancelRef = useRef(false);

  const pollOnce = async () => {
    try {
      const { data } = await api.get(`/bookings/${bookingId}/payment-status`);
      if (data.status === "paid") {
        setPhase("paid");
        try {
          const { data: b } = await api.get(`/bookings/${bookingId}`);
          setBooking(b);
        } catch {
          // ignore — still show success
        }
        return "paid";
      }
      return data.status || "unpaid";
    } catch (e) {
      setErrorMsg(formatApiErrorDetail(e?.response?.data?.detail) || e.message);
      setPhase("error");
      return "error";
    }
  };

  useEffect(() => {
    if (!bookingId) {
      setPhase("error");
      setErrorMsg("Missing booking_id");
      return;
    }
    let timer;
    let elapsed = 0;
    const tick = async () => {
      if (cancelRef.current) return;
      const status = await pollOnce();
      if (status === "paid" || status === "error") return;
      elapsed += 2000;
      if (elapsed >= 30000) {
        setPhase("timeout");
        return;
      }
      timer = setTimeout(tick, 2000);
    };
    // Kick off immediately, then poll on interval.
    tick();
    return () => {
      cancelRef.current = true;
      if (timer) clearTimeout(timer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bookingId]);

  const retry = async () => {
    setPhase("polling");
    setErrorMsg("");
    cancelRef.current = false;
    // restart from scratch
    setTimeout(() => {
      cancelRef.current = false;
      pollOnce().then((s) => {
        if (s !== "paid" && s !== "error") setPhase("timeout");
      });
    }, 0);
  };

  return (
    <div className="bg-background">
      <main className="mx-auto max-w-2xl px-6 py-16 text-center" data-testid="booking-success">
        {phase === "polling" && (
          <>
            <div className="mx-auto flex h-20 w-20 items-center justify-center rounded-full bg-brand-orange/10 text-brand-orange">
              <Loader2 className="h-10 w-10 animate-spin" />
            </div>
            <h1 className="mt-6 font-heading text-4xl font-bold text-pp-text sm:text-5xl">
              Confirming your payment&hellip;
            </h1>
            <p className="mt-3 text-sm text-muted-foreground">
              Stripe is processing the charge. This usually takes a few seconds.
            </p>
            {sessionId && (
              <p className="mt-4 text-xs text-muted-foreground" data-testid="booking-success-session">
                Session: <code>{sessionId.slice(0, 20)}…</code>
              </p>
            )}
          </>
        )}

        {phase === "paid" && (
          <>
            <div className="mx-auto flex h-20 w-20 items-center justify-center rounded-full bg-brand-orange text-white">
              <Check className="h-10 w-10" />
            </div>
            <h1 className="mt-6 font-heading text-4xl font-bold text-pp-text sm:text-5xl">
              You&apos;re booked!
            </h1>
            {booking && (
              <Card className="mt-8 rounded-3xl border-border bg-pp-surface p-6 text-left shadow-none">
                <p className="text-sm text-muted-foreground">
                  Date: <b className="text-pp-text">{booking.service_date}</b> · {booking.preferred_window.start}–{booking.preferred_window.end}
                </p>
                <p className="mt-1 text-sm text-muted-foreground">
                  Dogs: <b className="text-pp-text">{booking.dog_count}</b>
                </p>
                <p className="mt-3 font-heading text-2xl font-bold text-brand-orange">
                  Total paid: ${(booking.total_amount_cents / 100).toFixed(2)}
                </p>
              </Card>
            )}
            <div className="mt-8 flex justify-center gap-3">
              <Link to="/dashboard/customer">
                <Button
                  data-testid="booking-success-dashboard-btn"
                  className="rounded-full bg-pp-orange text-pp-cta-text hover:bg-pp-orange-hover"
                >
                  View in dashboard
                </Button>
              </Link>
              <Link to="/book">
                <Button data-testid="booking-success-book-another-btn" variant="ghost" className="rounded-full">
                  Book another
                </Button>
              </Link>
            </div>
          </>
        )}

        {phase === "timeout" && (
          <>
            <div className="mx-auto flex h-20 w-20 items-center justify-center rounded-full bg-yellow-100 text-yellow-700">
              <RefreshCw className="h-10 w-10" />
            </div>
            <h1 className="mt-6 font-heading text-4xl font-bold text-pp-text sm:text-5xl">
              Still confirming&hellip;
            </h1>
            <p className="mt-3 text-sm text-muted-foreground">
              Stripe is taking longer than usual. Try again, or check your dashboard.
            </p>
            <div className="mt-8 flex justify-center gap-3">
              <Button
                data-testid="booking-success-retry-btn"
                onClick={retry}
                className="rounded-full bg-pp-orange text-pp-cta-text hover:bg-pp-orange-hover"
              >
                Retry
              </Button>
              <Button
                data-testid="booking-success-dashboard-btn"
                variant="ghost"
                className="rounded-full"
                onClick={() => navigate("/dashboard/customer")}
              >
                Go to dashboard
              </Button>
            </div>
          </>
        )}

        {phase === "error" && (
          <>
            <div className="mx-auto flex h-20 w-20 items-center justify-center rounded-full bg-red-100 text-red-700">
              <XCircle className="h-10 w-10" />
            </div>
            <h1 className="mt-6 font-heading text-4xl font-bold text-pp-text sm:text-5xl">
              Couldn&apos;t confirm payment
            </h1>
            <p className="mt-3 text-sm text-muted-foreground" data-testid="booking-success-error-msg">
              {errorMsg}
            </p>
            <div className="mt-8 flex justify-center gap-3">
              <Button
                data-testid="booking-success-retry-btn"
                onClick={retry}
                className="rounded-full bg-pp-orange text-pp-cta-text hover:bg-pp-orange-hover"
              >
                Retry
              </Button>
              <Link to="/dashboard/customer">
                <Button variant="ghost" className="rounded-full">
                  Go to dashboard
                </Button>
              </Link>
            </div>
          </>
        )}
      </main>
    </div>
  );
}
